import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {DomainSetting} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
  options?: string[]; // Optional enum/array of options to render as checkboxes
}

@Component({
  selector: 'boolean-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
  ],
  templateUrl: './boolean-parameter.component.html',
})
export class BooleanParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  isObjectValue = false;
  booleanKeys: string[] = [];
  options?: string[]; // Enum/array of options to render

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    this.id = this.context.data.id;
    this.setting = this.context.data.setting;
    this.editSettingFn = this.context.data.editSettingFn;
    this.options = this.context.data.options;
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    let parsedValue: unknown = false;

    if (this.setting.value) {
      try {
        if (typeof this.setting.value === 'string') {
          parsedValue = JSON.parse(this.setting.value);
        } else {
          parsedValue = this.setting.value;
        }
      } catch (e) {
        // If parsing fails, treat as string boolean
        const strValue = String(this.setting.value).toLowerCase();
        parsedValue = strValue === 'true';
      }
    }

    // If options are provided, use them to render checkboxes
    if (this.options && this.options.length > 0) {
      this.isObjectValue = true;
      this.booleanKeys = this.options;
      const parsedObj =
        typeof parsedValue === 'object' &&
        parsedValue !== null &&
        !Array.isArray(parsedValue)
          ? (parsedValue as Record<string, unknown>)
          : {};
      const formControls: Record<string, [boolean]> = {};

      this.booleanKeys.forEach(key => {
        const value = parsedObj[key];
        formControls[key] = [
          typeof value === 'boolean'
            ? value
            : String(value).toLowerCase() === 'true',
        ];
      });

      this.formGroup = this.fb.group(formControls);
      return;
    }

    // Check if it's an object with boolean properties
    if (
      typeof parsedValue === 'object' &&
      parsedValue !== null &&
      !Array.isArray(parsedValue)
    ) {
      this.isObjectValue = true;
      const parsedObj = parsedValue as Record<string, unknown>;
      this.booleanKeys = Object.keys(parsedObj);
      const formControls: Record<string, [boolean]> = {};

      this.booleanKeys.forEach(key => {
        const value = parsedObj[key];
        formControls[key] = [
          typeof value === 'boolean'
            ? value
            : String(value).toLowerCase() === 'true',
        ];
      });

      this.formGroup = this.fb.group(formControls);
    } else {
      // Single boolean value
      this.isObjectValue = false;
      const boolValue =
        typeof parsedValue === 'boolean'
          ? parsedValue
          : String(parsedValue).toLowerCase() === 'true';

      this.formGroup = this.fb.group({
        value: [boolValue],
      });
    }
  }

  async onSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.submitting = true;

    let newValue: string | Record<string, unknown>;

    if (this.isObjectValue) {
      // For object values, create an object from form values
      newValue = this.formGroup.value as Record<string, unknown>;
    } else {
      // For single boolean, get the value from form
      const boolValue = this.formGroup.get('value')?.value || false;
      newValue = boolValue ? 'true' : 'false';
    }

    // Create updated setting with proper type
    const updatedSetting = {
      ...this.setting,
      value: newValue,
    } as RecipientSetting | DomainSetting;

    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: (error: Error) => {
        console.error('Error updating boolean setting:', error);
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close() {
    this.context.completeWith(false);
  }

  getSettingLabel(key: string): string {
    return this.translate.instant(
      `features.settings.parameters.${this.setting.key}.${key}.label`,
    );
  }
}
